/**
 * Coordinate transformation utilities
 * Handles conversion between feature-local and world coordinate systems
 */

/**
 * Convert axis-angle rotation to rotation matrix
 * @param {Object} rotation - Rotation as {rx, ry, rz} in radians (axis-angle representation)
 * @returns {Array<Array<number>>} 3x3 rotation matrix
 */
function axisAngleToRotationMatrix(rotation) {
  const { rx, ry, rz } = rotation;
  
  // Calculate angle (magnitude of rotation vector)
  const angle = Math.sqrt(rx * rx + ry * ry + rz * rz);
  
  // Handle zero rotation
  if (angle < 1e-10) {
    return [
      [1, 0, 0],
      [0, 1, 0],
      [0, 0, 1]
    ];
  }
  
  // Normalize axis
  const x = rx / angle;
  const y = ry / angle;
  const z = rz / angle;
  
  // Rodrigues' rotation formula
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const t = 1 - c;
  
  return [
    [t*x*x + c,   t*x*y - z*s, t*x*z + y*s],
    [t*x*y + z*s, t*y*y + c,   t*y*z - x*s],
    [t*x*z - y*s, t*y*z + x*s, t*z*z + c  ]
  ];
}

/**
 * Convert rotation matrix to axis-angle
 * @param {Array<Array<number>>} R - 3x3 rotation matrix
 * @returns {Object} Rotation as {rx, ry, rz} in radians
 */
function rotationMatrixToAxisAngle(R) {
  // Recovered via a quaternion rather than acos((trace - 1) / 2).
  //
  // The acos form goes flat as the rotation angle approaches PI, which is
  // exactly where real UR tool orientations live - a typical weld waypoint sits
  // at |r| = 3.13 rad against PI = 3.14159. Measured against exact values the
  // acos form errs by 3.6e-13 rad at |r| = 3.13, 8.2e-9 at PI - 1e-4 and
  // 1.4e-4 at PI - 1e-6. The quaternion route below is exact to machine
  // precision across the whole range and agrees with the old code to ~1e-15
  // everywhere the old code was accurate.
  const m00 = R[0][0], m01 = R[0][1], m02 = R[0][2];
  const m10 = R[1][0], m11 = R[1][1], m12 = R[1][2];
  const m20 = R[2][0], m21 = R[2][1], m22 = R[2][2];
  const trace = m00 + m11 + m22;

  // Branch on the largest of trace / diagonal so the sqrt is never near zero.
  let qw, qx, qy, qz, s;
  if (trace > 0) {
    s = Math.sqrt(trace + 1) * 2;
    qw = 0.25 * s;
    qx = (m21 - m12) / s;
    qy = (m02 - m20) / s;
    qz = (m10 - m01) / s;
  } else if (m00 > m11 && m00 > m22) {
    s = Math.sqrt(1 + m00 - m11 - m22) * 2;
    qw = (m21 - m12) / s;
    qx = 0.25 * s;
    qy = (m01 + m10) / s;
    qz = (m02 + m20) / s;
  } else if (m11 > m22) {
    s = Math.sqrt(1 + m11 - m00 - m22) * 2;
    qw = (m02 - m20) / s;
    qx = (m01 + m10) / s;
    qy = 0.25 * s;
    qz = (m12 + m21) / s;
  } else {
    s = Math.sqrt(1 + m22 - m00 - m11) * 2;
    qw = (m10 - m01) / s;
    qx = (m02 + m20) / s;
    qy = (m12 + m21) / s;
    qz = 0.25 * s;
  }

  const norm = Math.sqrt(qx * qx + qy * qy + qz * qz);
  if (norm < 1e-15) {
    return { rx: 0, ry: 0, rz: 0 };
  }

  // atan2 stays well conditioned at both ends of the range.
  let angle = 2 * Math.atan2(norm, qw);
  if (angle > Math.PI) {
    // Take the short way round; the negative angle flips the axis with it,
    // giving the same rotation with |angle| <= PI, as PolyScope expects.
    angle -= 2 * Math.PI;
  }

  const k = angle / norm;
  return { rx: qx * k, ry: qy * k, rz: qz * k };
}

/**
 * Multiply two 3x3 rotation matrices
 * @param {Array<Array<number>>} R1 - First rotation matrix
 * @param {Array<Array<number>>} R2 - Second rotation matrix
 * @returns {Array<Array<number>>} Result rotation matrix
 */
function multiplyRotationMatrices(R1, R2) {
  const result = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0]
  ];
  
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      for (let k = 0; k < 3; k++) {
        result[i][j] += R1[i][k] * R2[k][j];
      }
    }
  }
  
  return result;
}

/**
 * Transpose a 3x3 matrix
 * @param {Array<Array<number>>} R - Rotation matrix
 * @returns {Array<Array<number>>} Transposed matrix
 */
function transposeMatrix(R) {
  return [
    [R[0][0], R[1][0], R[2][0]],
    [R[0][1], R[1][1], R[2][1]],
    [R[0][2], R[1][2], R[2][2]]
  ];
}

/**
 * Apply rotation matrix to a position vector
 * @param {Array<Array<number>>} R - Rotation matrix
 * @param {Object} pos - Position {x, y, z}
 * @returns {Object} Rotated position
 */
function applyRotationToPosition(R, pos) {
  return {
    x: R[0][0] * pos.x + R[0][1] * pos.y + R[0][2] * pos.z,
    y: R[1][0] * pos.x + R[1][1] * pos.y + R[1][2] * pos.z,
    z: R[2][0] * pos.x + R[2][1] * pos.y + R[2][2] * pos.z
  };
}

/**
 * Convert from feature coordinates to world coordinates
 * @param {Object} position - Position in feature frame
 * @param {Object} rotation - Rotation in feature frame
 * @param {Object} feature - Feature definition
 * @returns {Object} World position and rotation
 */
export function featureToWorld(position, rotation, feature) {
  // Get feature rotation matrix
  const R_feature = axisAngleToRotationMatrix(feature.rotation);
  
  // Rotate the local position by the feature's rotation
  const rotatedPos = applyRotationToPosition(R_feature, position);
  
  // Add feature position (translation)
  const worldPosition = {
    x: feature.position.x + rotatedPos.x,
    y: feature.position.y + rotatedPos.y,
    z: feature.position.z + rotatedPos.z
  };
  
  // Combine rotations: R_world = R_feature * R_local
  const R_local = axisAngleToRotationMatrix(rotation);
  const R_world = multiplyRotationMatrices(R_feature, R_local);
  const worldRotation = rotationMatrixToAxisAngle(R_world);
  
  return {
    position: worldPosition,
    rotation: worldRotation
  };
}

/**
 * Convert from world coordinates to feature coordinates
 * @param {Object} position - Position in world frame
 * @param {Object} rotation - Rotation in world frame
 * @param {Object} feature - Feature definition
 * @returns {Object} Feature position and rotation
 */
export function worldToFeature(position, rotation, feature) {
  // Get feature rotation matrix and its transpose (inverse for rotation matrices)
  const R_feature = axisAngleToRotationMatrix(feature.rotation);
  const R_feature_inv = transposeMatrix(R_feature);
  
  // Subtract feature position
  const relativePos = {
    x: position.x - feature.position.x,
    y: position.y - feature.position.y,
    z: position.z - feature.position.z
  };
  
  // Rotate by inverse of feature rotation
  const localPosition = applyRotationToPosition(R_feature_inv, relativePos);
  
  // Split rotations: R_local = R_feature^T * R_world
  const R_world = axisAngleToRotationMatrix(rotation);
  const R_local = multiplyRotationMatrices(R_feature_inv, R_world);
  const localRotation = rotationMatrixToAxisAngle(R_local);
  
  return {
    position: localPosition,
    rotation: localRotation
  };
}

/**
 * Convert waypoints to world coordinates for visualization
 * @param {Array<Object>} waypoints - Waypoints in feature-local coordinates
 * @param {Array<Object>} features - Array of features
 * @returns {Array<Object>} Waypoints in world coordinates
 */
export function convertWaypointsToWorldCoords(waypoints, features = []) {
  return waypoints.map(waypoint => {
    // Find the feature for this waypoint
    const feature = features.find(f => f.name === waypoint.feature);

    if (!feature) {
      // If feature not found (e.g., features not loaded), return waypoint as-is
      // The waypoint position should already be in robot coordinates
      return waypoint;
    }

    // Convert waypoint to world coordinates
    const worldCoords = featureToWorld(waypoint.position, waypoint.rotation, feature);

    // Convert via-point to world coordinates if it exists
    let viaPointWorld = null;
    if (waypoint.viaPoint) {
      const viaWorldCoords = featureToWorld(
        waypoint.viaPoint.position,
        waypoint.viaPoint.rotation,
        feature
      );
      viaPointWorld = {
        ...waypoint.viaPoint,
        position: viaWorldCoords.position,
        rotation: viaWorldCoords.rotation
      };
    }

    // Return a copy with world coordinates
    return {
      ...waypoint,
      position: worldCoords.position,
      rotation: worldCoords.rotation,
      viaPoint: viaPointWorld
    };
  });
}

/**
 * Mirror a position across a plane in feature-local coordinates
 * @param {Object} position - Position {x, y, z} in feature-local coordinates
 * @param {string} plane - Plane type ('xy', 'xz', or 'yz')
 * @returns {Object} Mirrored position
 */
export function mirrorPositionAcrossPlane(position, plane) {
  const mirrored = { ...position };

  switch (plane) {
    case 'xy':
      // Mirror across XY plane (flip Z)
      mirrored.z = -mirrored.z;
      break;
    case 'xz':
      // Mirror across XZ plane (flip Y)
      mirrored.y = -mirrored.y;
      break;
    case 'yz':
      // Mirror across YZ plane (flip X)
      mirrored.x = -mirrored.x;
      break;
  }

  return mirrored;
}

/**
 * Mirror a rotation across a plane in feature-local coordinates
 * @param {Object} rotation - Rotation {rx, ry, rz} in radians (axis-angle)
 * @param {string} plane - Plane type ('xy', 'xz', or 'yz')
 * @returns {Object} Mirrored rotation
 */
export function mirrorRotationAcrossPlane(rotation, plane) {
  // Convert to rotation matrix
  const R = axisAngleToRotationMatrix(rotation);

  // Apply 180° rotation around world Z-axis
  // This maintains the proper tool orientation relationship with the mirror plane
  const R_worldZ_180 = [
    [-1, 0, 0],
    [0, -1, 0],
    [0, 0, 1]
  ];

  // Apply: R_mirrored = R_worldZ_180 * R
  const R_mirrored = multiplyRotationMatrices(R_worldZ_180, R);

  // Convert back to axis-angle
  return rotationMatrixToAxisAngle(R_mirrored);
}

